import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorState, FullPageSpinner, cx, Badge, buttonClass, Breadcrumb, LinkButton } from "@/components/ui";
import { domainsApi, longDate, type Order, type OrderItem, type PillTone } from "@/lib/domains";
import { formatMoney } from "@/lib/format";
import { DomainIcon } from "@/components/domains/parts";

const STATUS: Record<Order["status"], { label: string; tone: PillTone }> = {
  pending_payment: { label: "Awaiting payment", tone: "warning" },
  payment_failed: { label: "Payment failed", tone: "danger" },
  paid: { label: "Processing", tone: "warning" },
  processing: { label: "Processing", tone: "warning" },
  completed: { label: "Completed", tone: "success" },
  partially_failed: { label: "Partly refunded", tone: "warning" },
  failed: { label: "Failed · refunded", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

const KIND: Record<OrderItem["kind"], string> = {
  register: "Registration",
  transfer: "Transfer",
  renew: "Renewal",
  restore: "Recovery",
};

const COLS = "grid grid-cols-[140px_120px_minmax(220px,1fr)_110px_150px_120px] items-center";

/** `/domains/orders`: every domain order of the workspace, newest first, styled like the domain table. */
export function DomainOrdersPage() {
  const { orgId } = useOrganization();
  const orders = useQuery({
    queryKey: ["domains", orgId, "orders"],
    queryFn: () => domainsApi.orders(orgId),
    refetchInterval: (q) =>
      q.state.data?.orders.some((o) => ["pending_payment", "paid", "processing"].includes(o.status))
        ? 5000
        : false,
  });

  if (orders.isPending) return <FullPageSpinner />;
  if (orders.isError) return <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />;
  const list = orders.data.orders;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: "Domains", to: "/domains" }, { label: "Order history" }]} />
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-2xl font-bold text-fg">Order history</h1>
          <p className="text-sm text-fg-subtle">
            Domain registrations, transfers, renewals and recoveries · prices include VAT
          </p>
        </div>
        <LinkButton to="/domains/find" className={buttonClass("brand", "pill")} requires="domains:manage">
          + Find a domain
        </LinkButton>
      </div>

      {list.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-[18px] border border-border bg-surface px-6 py-14 text-center">
          <span className="flex size-11 items-center justify-center rounded-xl bg-[#faf6ef]">
            <DomainIcon name="globe" size={24} />
          </span>
          <p className="text-base font-semibold text-fg">No domain orders yet</p>
          <p className="max-w-md text-sm text-fg-subtle">
            When you register, transfer, renew or recover a domain, the order and its invoice appear here.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[18px] border border-border bg-surface">
          <div className="min-w-[880px]">
            <div
              className={cx(COLS, "px-[18px] pt-3 pb-2.5 text-[11px] font-semibold text-fg-subtle uppercase")}
            >
              <span>Order</span>
              <span>Date</span>
              <span>Domains</span>
              <span>Total</span>
              <span>Status</span>
              <span />
            </div>
            {list.map((o) => (
              <OrderRow key={o.id} order={o} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OrderRow({ order: o }: { order: Order }) {
  const status = STATUS[o.status];
  const first = o.items[0];
  const more = o.items.length - 1;
  return (
    <>
      <div className="h-px bg-border" />
      <div className={cx(COLS, "px-[18px] py-3")}>
        <Link
          to={`/domains/orders/${o.id}`}
          className="truncate pr-2 text-[13.5px] font-semibold text-fg hover:underline"
        >
          #{o.number}
        </Link>
        <span className="text-[12.5px] whitespace-nowrap text-fg">{longDate(o.created_at)}</span>
        <div className="flex min-w-0 flex-col gap-px pr-3">
          <span className="truncate text-[13px] text-fg">
            {first?.fqdn ?? "—"}
            {more > 0 && <span className="text-fg-subtle"> + {more} more</span>}
          </span>
          <span className="truncate text-[11.5px] text-fg-subtle">
            {[...new Set(o.items.map((i) => KIND[i.kind]))].join(", ")}
          </span>
        </div>
        <span className="text-[12.5px] font-semibold text-fg">{formatMoney(o.total_cents, o.currency)}</span>
        <span>
          <Badge tone={status.tone} size="sm">
            {status.label}
          </Badge>
        </span>
        <span className="flex justify-end gap-3 text-[12.5px] font-semibold whitespace-nowrap">
          {o.invoice_id && (
            <Link
              to={`/billing/invoices/${o.invoice_id}`}
              className="text-fg-muted hover:text-fg hover:underline"
            >
              Invoice
            </Link>
          )}
          <Link to={`/domains/orders/${o.id}`} className="text-brand-500 hover:underline">
            View →
          </Link>
        </span>
      </div>
    </>
  );
}
